/* © NXAPP Limited */
import { useState, Suspense, useEffect, useRef, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { supabase } from '@/lib/supabase'
import { lazyWithReload } from '@/lib/lazyWithReload'
import { describeError } from '@/lib/describeError'
import { useAuthContext } from '@/context/AuthContext'
import { Save, Send, ArrowLeft, Check, Clock, PenLine, Type, FileText, Users, Megaphone, Layers, Lock } from 'lucide-react'
import Spinner from '@/components/ui/Spinner'
import { PLAIN_STARTER_HTML } from '@/components/builder/plainStarter'
import MergeTagHelp from '@/components/builder/MergeTagHelp'
import type { IssueRow, IssueAudience, SegmentRow, SendingProfileRow } from '@/types/rows'
import { authedFetch } from '@/lib/authedFetch'

const EmailBuilder = lazyWithReload(() => import('@/components/builder/EmailBuilder'))
const SimpleEmailEditor = lazyWithReload(() => import('@/components/builder/SimpleEmailEditor'))
const DossierEditor = lazyWithReload(() => import('@/components/builder/DossierEditor'))

/** design_json marker: this issue/template uses the plain editor, not the designer. */
const SIMPLE_MODE = { mode: 'simple' } as const
const DOSSIER_MODE = { mode: 'dossier' } as const
const isSimple = (d: Record<string, unknown> | undefined) => d?.mode === 'simple'
const isDossier = (d: Record<string, unknown> | undefined) => d?.mode === 'dossier'

const schema = z.object({
  subject: z.string().min(1, 'Subject is required'),
  preheader: z.string().optional(),
  sending_profile_id: z.string().optional(),
})
type IssueForm = z.infer<typeof schema>

type AutosaveStatus = 'idle' | 'saving' | 'saved' | 'error'

// ─── Template picker ──────────────────────────────────────────────────────────

interface TemplateRow { id: string; name: string; design_json: Record<string, unknown>; html: string; campaign_id: string | null }

function TemplatePicker({
  campaignId,
  onPick,
}: {
  campaignId?: string
  onPick: (design: Record<string, unknown>, html: string) => void
}) {
  const { user } = useAuthContext()

  const { data: templates, isLoading } = useQuery({
    queryKey: ['templates', user?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from('templates')
        .select('id, name, design_json, html, campaign_id')
        .eq('user_id', user!.id)
        .order('created_at', { ascending: true })
      return (data ?? []) as TemplateRow[]
    },
    enabled: !!user,
  })

  // Campaign-specific templates first, then shared (null campaign_id)
  const campaignTemplates = (templates ?? []).filter(t => t.campaign_id === campaignId)
  const sharedTemplates   = (templates ?? []).filter(t => t.campaign_id === null)
  const ordered = [...campaignTemplates, ...sharedTemplates]

  const ICONS = ['📄', '📨', '✨', '📰', '🎯', '💡', '🚀', '📊']

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">Start with a template</h2>
        <p className="text-sm text-gray-500 mt-1">Pick a starting point — you can customise everything in the editor.</p>
      </div>
      {isLoading ? (
        <div className="flex justify-center py-12"><Spinner /></div>
      ) : (
        <>
          {campaignId && campaignTemplates.length > 0 && (
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">This campaign</p>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <button
              type="button"
              onClick={() => onPick({ ...SIMPLE_MODE }, PLAIN_STARTER_HTML)}
              className="card p-5 flex flex-col items-start gap-3 text-left hover:border-brand-400 hover:shadow-md transition-all group"
            >
              <div className="w-12 h-12 rounded-xl bg-gray-100 group-hover:bg-brand-50 flex items-center justify-center transition-colors">
                <Type className="w-5 h-5 text-gray-500 group-hover:text-brand-600" />
              </div>
              <div>
                <p className="font-semibold text-gray-900">Plain email</p>
                <p className="text-xs text-gray-400 mt-1">Just type — like Outlook</p>
              </div>
            </button>
            <button
              type="button"
              onClick={() => onPick({}, '')}
              className="card p-5 flex flex-col items-start gap-3 text-left hover:border-brand-400 hover:shadow-md transition-all group"
            >
              <div className="w-12 h-12 rounded-xl bg-gray-100 group-hover:bg-brand-50 flex items-center justify-center transition-colors">
                <PenLine className="w-5 h-5 text-gray-500 group-hover:text-brand-600" />
              </div>
              <div>
                <p className="font-semibold text-gray-900">Designer</p>
                <p className="text-xs text-gray-400 mt-1">Drag-and-drop blocks</p>
              </div>
            </button>
            <button
              type="button"
              onClick={() => onPick({ ...DOSSIER_MODE }, '')}
              className="card p-5 flex flex-col items-start gap-3 text-left hover:border-brand-400 hover:shadow-md transition-all group"
            >
              <div className="w-12 h-12 rounded-xl bg-gray-100 group-hover:bg-brand-50 flex items-center justify-center transition-colors">
                <FileText className="w-5 h-5 text-gray-500 group-hover:text-brand-600" />
              </div>
              <div>
                <p className="font-semibold text-gray-900">The Dossier</p>
                <p className="text-xs text-gray-400 mt-1">Weekly briefing — fill the fields</p>
              </div>
            </button>
            {ordered.map((t, i) => (
              <button
                key={t.id}
                type="button"
                onClick={() => onPick(t.design_json ?? {}, t.html ?? '')}
                className="card p-5 flex flex-col items-start gap-3 text-left hover:border-brand-400 hover:shadow-md transition-all group"
              >
                <div className="w-12 h-12 rounded-xl bg-brand-50 group-hover:bg-brand-100 flex items-center justify-center text-2xl transition-colors select-none">
                  {ICONS[i % ICONS.length]}
                </div>
                <div>
                  <p className="font-semibold text-gray-900">{t.name}</p>
                  <p className="text-xs text-gray-400 mt-1">
                    {t.campaign_id ? 'Campaign template' : 'Shared template'}
                  </p>
                </div>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

// ─── Main editor ──────────────────────────────────────────────────────────────

export default function IssueEditorPage() {
  // campaignId is in the URL for all campaign-scoped issue routes
  const { id, campaignId } = useParams<{ id?: string; campaignId?: string }>()
  const isNew = !id
  const { user } = useAuthContext()
  const navigate = useNavigate()
  const qc = useQueryClient()

  const backPath = campaignId ? `/campaigns/${campaignId}` : '/campaigns'

  const [html, setHtml] = useState('')
  const [design, setDesign] = useState<Record<string, unknown>>({})
  // Audience — union of ticked sources, deduped at send time
  const [audience, setAudience] = useState<Required<IssueAudience>>({
    campaignLeads: !!campaignId, allSubscribed: false, segmentIds: [],
  })
  const audienceRef = useRef(audience)
  // When the campaign has a default audience, the per-issue picker starts locked
  // to it — override is a deliberate action, not a stray click.
  const [audienceOverride, setAudienceOverride] = useState(false)
  useEffect(() => { audienceRef.current = audience }, [audience])
  const [templatePicked, setTemplatePicked] = useState(!isNew)
  const [testEmail, setTestEmail] = useState('')
  const [testSent, setTestSent] = useState(false)
  const [autosaveStatus, setAutosaveStatus] = useState<AutosaveStatus>('idle')

  const autosaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const currentIdRef = useRef<string | undefined>(id)
  useEffect(() => { currentIdRef.current = id }, [id])

  const { data: issue, isLoading: issueLoading } = useQuery({
    queryKey: ['issue', id],
    queryFn: async () => {
      const { data } = await supabase.from('issues').select('*').eq('id', id!).single()
      const row = data as IssueRow | null
      if (row) {
        setHtml(row.html); setDesign(row.design_json ?? {})
        // Initialise audience: stored value → legacy segment_id → everyone
        if (row.audience) {
          setAudience({
            campaignLeads: !!row.audience.campaignLeads,
            allSubscribed: !!row.audience.allSubscribed,
            segmentIds: row.audience.segmentIds ?? [],
          })
        } else if (row.segment_id) {
          setAudience({ campaignLeads: false, allSubscribed: false, segmentIds: [row.segment_id] })
        } else {
          setAudience({ campaignLeads: false, allSubscribed: true, segmentIds: [] })
        }
      }
      return row
    },
    enabled: !isNew && !!id,
  })

  const { data: segments } = useQuery({
    queryKey: ['segments', user?.id],
    queryFn: async () => {
      const { data } = await supabase.from('segments').select('id, name').eq('user_id', user!.id)
      return (data ?? []) as Pick<SegmentRow, 'id' | 'name'>[]
    },
    enabled: !!user,
  })

  // Live counts for the audience picker
  const { data: subscribedCount } = useQuery({
    queryKey: ['subscribed-count', user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { count } = await supabase.from('contacts')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', user!.id).eq('status', 'SUBSCRIBED')
      return count ?? 0
    },
  })
  // A new issue inherits the campaign's default audience (e.g. The Dossier →
  // its Agents segment). Existing issues keep their own saved audience.
  const { data: campaignDefault } = useQuery({
    queryKey: ['campaign-default-audience', campaignId],
    enabled: !!campaignId && !!user,
    queryFn: async () => {
      const { data } = await supabase.from('campaigns')
        .select('default_audience').eq('id', campaignId!).single()
      return (data?.default_audience ?? null) as Required<IssueAudience> | null
    },
  })
  const seededRef = useRef(false)
  useEffect(() => {
    if (isNew && campaignDefault && !seededRef.current) {
      seededRef.current = true
      setAudience({
        campaignLeads: !!campaignDefault.campaignLeads,
        allSubscribed: !!campaignDefault.allSubscribed,
        segmentIds: campaignDefault.segmentIds ?? [],
      })
    }
  }, [isNew, campaignDefault])

  const { data: campaignLeadsCount } = useQuery({
    queryKey: ['campaign-leads-count', campaignId],
    enabled: !!campaignId && !!user,
    queryFn: async () => {
      const { count } = await supabase.from('lead_campaign_assignments')
        .select('id', { count: 'exact', head: true })
        .eq('campaign_id', campaignId!).eq('user_id', user!.id).eq('status', 'active')
      return count ?? 0
    },
  })

  const { data: profiles } = useQuery({
    queryKey: ['sending-profiles', user?.id],
    queryFn: async () => {
      const { data } = await supabase.from('sending_profiles')
        .select('id, name, from_email, from_name, is_default, company_name, company_address, company_website')
        .eq('user_id', user!.id)
      return (data ?? []) as Pick<SendingProfileRow,
        'id' | 'name' | 'from_email' | 'from_name' | 'is_default' | 'company_name' | 'company_address' | 'company_website'>[]
    },
    enabled: !!user,
  })

  const { register, handleSubmit, getValues, watch, formState: { errors, isSubmitting } } = useForm<IssueForm>({
    resolver: zodResolver(schema),
    values: issue ? {
      subject: issue.subject,
      preheader: issue.preheader ?? '',
      sending_profile_id: issue.sending_profile_id ?? '',
    } : undefined,
  })

  // Preview identity — mirrors the server's resolution (issue's profile → default).
  // Values are NOT baked into the HTML; merge tags stay in the saved content so
  // editing the sending profile updates every email and template automatically.
  const selectedProfileId = watch('sending_profile_id')
  const previewProfile =
    (profiles ?? []).find(p => p.id === selectedProfileId) ??
    (profiles ?? []).find(p => p.is_default) ??
    (profiles ?? [])[0]
  const previewIdentity = {
    companyName: previewProfile?.company_name?.trim() || previewProfile?.from_name || '',
    companyAddress: previewProfile?.company_address?.trim() || '',
    companyWebsite: previewProfile?.company_website?.trim() || '',
  }

  // ── Manual save ──────────────────────────────────────────────────────────────
  const save = useMutation({
    mutationFn: async (data: IssueForm) => {
      // Convert empty strings to null — Postgres rejects "" for UUID FK columns
      const payload = {
        ...data,
        segment_id: null,                       // superseded by audience
        audience: audienceRef.current,
        sending_profile_id: data.sending_profile_id || null,
        html,
        design_json: design,
        user_id: user!.id,
        campaign_id: campaignId ?? null,
      }
      if (isNew) {
        const { data: created, error } = await supabase.from('issues')
          .insert({ ...payload, status: 'DRAFT' }).select().single()
        if (error) throw error
        return created as IssueRow
      } else {
        const { data: updated, error } = await supabase.from('issues').update(payload).eq('id', id!).select().single()
        if (error) throw error
        return updated as IssueRow
      }
    },
    onSuccess: () => {
      // Manual "Save draft" is an explicit finish action — confirm by returning
      // to the list. (Autosave keeps you in the editor; this button does not.)
      qc.invalidateQueries({ queryKey: ['issues'] })
      setAutosaveStatus('saved')
      navigate(backPath)
    },
  })

  // ── Autosave (existing issues only, 4 s debounce) ────────────────────────────
  const autosave = useCallback(async (currentHtml: string, currentDesign: Record<string, unknown>) => {
    const tid = currentIdRef.current
    if (!tid || !user) return
    setAutosaveStatus('saving')
    const vals = getValues()
    const { error } = await supabase
      .from('issues')
      .update({
        html: currentHtml,
        design_json: currentDesign,
        subject: vals.subject || undefined,
        preheader: vals.preheader || null,
        segment_id: null,
        audience: audienceRef.current,
        sending_profile_id: vals.sending_profile_id || null,
      })
      .eq('id', tid)
      .eq('user_id', user.id)
    setAutosaveStatus(error ? 'error' : 'saved')
    if (!error) setTimeout(() => setAutosaveStatus('idle'), 2500)
  }, [user, getValues])

  const handleEditorChange = useCallback((h: string, d: Record<string, unknown>) => {
    setHtml(h)
    setDesign(d)
    if (currentIdRef.current) {
      if (autosaveTimer.current) clearTimeout(autosaveTimer.current)
      autosaveTimer.current = setTimeout(() => autosave(h, d), 4000)
    }
  }, [autosave])

  // ── Test send ────────────────────────────────────────────────────────────────
  const sendTest = useMutation({
    mutationFn: async () => {
      const res = await authedFetch('/api/send-test', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ issueId: id, toEmail: testEmail }),
      })
      if (!res.ok) throw new Error(await res.text())
    },
    onSuccess: () => setTestSent(true),
  })

  if (!isNew && issueLoading) {
    return <div className="flex justify-center py-20"><Spinner /></div>
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => templatePicked && isNew ? setTemplatePicked(false) : navigate(backPath)}
          className="btn-secondary p-2"
        >
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div className="flex-1">
          <h1 className="text-xl font-bold text-gray-900">{isNew ? 'New issue' : 'Edit issue'}</h1>
          {issue && <p className="text-sm text-gray-500 capitalize">{issue.status.toLowerCase().replace(/_/g, ' ')}</p>}
          {isNew && templatePicked && (
            <p className="text-xs text-gray-400 mt-0.5">← Back to template picker</p>
          )}
        </div>

        {/* Autosave indicator */}
        {!isNew && autosaveStatus !== 'idle' && (
          <p className={`text-xs flex items-center gap-1 ${
            autosaveStatus === 'error' ? 'text-red-500' :
            autosaveStatus === 'saving' ? 'text-gray-400' : 'text-green-600'
          }`}>
            {autosaveStatus === 'saving' && <><Clock className="w-3 h-3 animate-spin" /> Saving…</>}
            {autosaveStatus === 'saved'  && <><Check className="w-3 h-3" /> Autosaved</>}
            {autosaveStatus === 'error'  && 'Autosave failed — save manually'}
          </p>
        )}
      </div>

      {/* Step 1: template picker (new issues only) */}
      {!templatePicked ? (
        <div className="card p-6">
          <TemplatePicker
            campaignId={campaignId}
            onPick={(d, h) => { setDesign(d); setHtml(h); setTemplatePicked(true) }}
          />
        </div>
      ) : (
        /* Step 2: editor form */
        <form onSubmit={handleSubmit(d => save.mutate(d))} className="space-y-6">
          {issue && (issue.status === 'SENT' || issue.status === 'SENDING') && (
            <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3">
              <p className="text-sm font-semibold text-amber-900">⚠ This issue has already been sent</p>
              <p className="text-xs text-amber-800 mt-1">
                The emails are delivered and cannot be recalled or changed. Editing here updates the
                <strong> web archive</strong> only — the “view in browser” copy — so you can correct a
                mistake for anyone who opens it from the link.
                {issue.status === 'SENT' && (
                  <> View it at <a href={`/i/${issue.id}`} target="_blank" rel="noopener noreferrer" className="underline">/i/{issue.id.slice(0, 8)}…</a></>
                )}
              </p>
            </div>
          )}
          <div className="card p-5 grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="label">Subject line *</label>
              <input className="input" placeholder="This week in tech: AI, London funding & your Friday tip" {...register('subject')} />
              <MergeTagHelp />
              {errors.subject && <p className="mt-1 text-xs text-red-600">{errors.subject.message}</p>}
            </div>
            <div className="col-span-2">
              <label className="label">Preheader (preview text)</label>
              <input className="input" placeholder="Short summary shown after the subject in the inbox…" {...register('preheader')} />
            </div>
            {/* Audience — union of ticked sources, deduped by email at send time */}
            <div className="col-span-2">
              {(() => {
                const dflt = campaignDefault
                const hasDefault = !!dflt && (!!dflt.campaignLeads || !!dflt.allSubscribed || (dflt.segmentIds?.length ?? 0) > 0)
                const locked = hasDefault && !audienceOverride
                if (!locked) return (
                  <div className="flex items-center gap-2 mb-1">
                    <label className="label mb-0">Send to</label>
                    {hasDefault && (
                      <button type="button"
                        onClick={() => {
                          // Snap back to the campaign default and re-lock.
                          setAudience({
                            campaignLeads: !!dflt!.campaignLeads,
                            allSubscribed: !!dflt!.allSubscribed,
                            segmentIds: dflt!.segmentIds ?? [],
                          })
                          setAudienceOverride(false)
                        }}
                        className="text-[11px] font-medium text-gray-500 hover:text-brand-600">
                        ↺ Use campaign default
                      </button>
                    )}
                  </div>
                )
                // Locked view — read-only summary of the inherited default.
                const chips: string[] = [
                  ...(audience.campaignLeads ? ["This campaign's leads"] : []),
                  ...(audience.allSubscribed ? ['All subscribed members'] : []),
                  ...(segments ?? []).filter(s => audience.segmentIds.includes(s.id)).map(s => s.name),
                ]
                return (
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <label className="label mb-0">Send to</label>
                      <span className="text-[10px] text-gray-400">— campaign default</span>
                      <button type="button"
                        onClick={() => setAudienceOverride(true)}
                        className="ml-auto flex items-center gap-1 text-[11px] font-medium text-gray-500 hover:text-brand-600 px-2 py-0.5 rounded border border-gray-200 hover:border-brand-300 transition-colors"
                        title="Change the audience for this issue only">
                        <Lock className="w-3 h-3" /> Override for this issue
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {chips.length ? chips.map(c => (
                        <span key={c} className="text-[11px] px-2.5 py-1 rounded-full border font-medium bg-indigo-50 text-indigo-700 border-indigo-200">{c}</span>
                      )) : <span className="text-[11px] text-amber-600">Campaign default is empty</span>}
                    </div>
                    <p className="text-[10px] text-gray-400 mt-1.5">Locked to the campaign default. Override to target a different audience for this issue only.</p>
                  </div>
                )
              })()}
              {(() => {
                const dflt = campaignDefault
                const hasDefault = !!dflt && (!!dflt.campaignLeads || !!dflt.allSubscribed || (dflt.segmentIds?.length ?? 0) > 0)
                if (hasDefault && !audienceOverride) return null
                return (
              <><div className="space-y-1.5 mt-1">
                {campaignId && (
                  <label className="flex items-center gap-3 px-3 py-2 rounded-lg border border-gray-200 hover:border-brand-300 cursor-pointer transition-colors">
                    <input type="checkbox" checked={audience.campaignLeads}
                      onChange={() => setAudience(a => ({ ...a, campaignLeads: !a.campaignLeads }))}
                      className="rounded border-gray-300 text-brand-600 focus:ring-brand-500" />
                    <Megaphone className="w-3.5 h-3.5 text-brand-500 shrink-0" />
                    <span className="text-xs font-medium text-gray-800">This campaign's leads</span>
                    <span className="text-[10px] text-gray-400 ml-auto">{campaignLeadsCount ?? '…'} linked</span>
                  </label>
                )}
                <label className="flex items-center gap-3 px-3 py-2 rounded-lg border border-gray-200 hover:border-brand-300 cursor-pointer transition-colors">
                  <input type="checkbox" checked={audience.allSubscribed}
                    onChange={() => setAudience(a => ({ ...a, allSubscribed: !a.allSubscribed }))}
                    className="rounded border-gray-300 text-brand-600 focus:ring-brand-500" />
                  <Users className="w-3.5 h-3.5 text-gray-500 shrink-0" />
                  <span className="text-xs font-medium text-gray-800">All subscribed members</span>
                  <span className="text-[10px] text-gray-400 ml-auto">{subscribedCount ?? '…'} contacts</span>
                </label>
                {(segments ?? []).map(s => (
                  <label key={s.id} className="flex items-center gap-3 px-3 py-2 rounded-lg border border-gray-200 hover:border-brand-300 cursor-pointer transition-colors">
                    <input type="checkbox" checked={audience.segmentIds.includes(s.id)}
                      onChange={() => setAudience(a => ({
                        ...a,
                        segmentIds: a.segmentIds.includes(s.id)
                          ? a.segmentIds.filter(x => x !== s.id)
                          : [...a.segmentIds, s.id],
                      }))}
                      className="rounded border-gray-300 text-brand-600 focus:ring-brand-500" />
                    <Layers className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                    <span className="text-xs text-gray-700">{s.name}</span>
                  </label>
                ))}
              </div>
              <p className="text-[10px] text-gray-400 mt-1.5">
                {!audience.campaignLeads && !audience.allSubscribed && audience.segmentIds.length === 0
                  ? '⚠ Nothing selected — this issue will send to no one'
                  : audience.allSubscribed
                    ? 'Sends to every subscribed contact (other ticks are already included)'
                    : 'Recipients are combined and de-duplicated by email; unsubscribed contacts are always excluded'}
              </p></>
                )
              })()}
            </div>
            <div>
              <label className="label">Sending profile</label>
              <select className="input" {...register('sending_profile_id')}>
                <option value="">Campaign default</option>
                {profiles?.map(p => <option key={p.id} value={p.id}>{p.name} ({p.from_email})</option>)}
              </select>
              <p className="mt-1 text-xs text-gray-400">Leave on “Campaign default” to send from the campaign's profile.</p>
            </div>
          </div>

          {/* Email builder */}
          <div>
            <h2 className="text-sm font-semibold text-gray-700 mb-2">Email content</h2>
            <Suspense fallback={<div className="flex justify-center py-20 border border-gray-200 rounded-lg"><Spinner /></div>}>
              {isDossier(design) ? (
                <DossierEditor
                  initialData={(design.data as Record<string, unknown>) as never}
                  campaignId={campaignId}
                  userId={user?.id}
                  onChange={(h, d) => handleEditorChange(h, { ...DOSSIER_MODE, data: d })}
                />
              ) : isSimple(design) ? (
                <SimpleEmailEditor
                  initialHtml={html}
                  identity={previewIdentity}
                  onChange={h => handleEditorChange(h, { ...SIMPLE_MODE })}
                />
              ) : (
                <EmailBuilder
                  initialHtml={html}
                  initialDesign={Object.keys(design).length ? design : undefined}
                  onChange={handleEditorChange}
                />
              )}
            </Suspense>
          </div>

          {/* Test send */}
          {!isNew && (
            <div className="card p-4 flex items-end gap-3">
              <div className="flex-1">
                <label className="label">Send test email</label>
                <input
                  className="input"
                  type="email"
                  placeholder="you@company.com"
                  value={testEmail}
                  onChange={e => setTestEmail(e.target.value)}
                />
              </div>
              <button
                type="button"
                disabled={!testEmail || sendTest.isPending}
                onClick={() => sendTest.mutate()}
                className="btn-secondary"
              >
                <Send className="w-4 h-4" />
                {sendTest.isPending ? 'Sending…' : 'Send test'}
              </button>
              {testSent && <p className="text-sm text-green-600">Test sent!</p>}
              {sendTest.isError && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2">
                  <p className="text-xs font-semibold text-red-800">Failed to send</p>
                  <p className="mt-0.5 text-xs text-red-700 font-mono break-all whitespace-pre-wrap">
                    {sendTest.error instanceof Error && sendTest.error.message
                      ? sendTest.error.message
                      : 'No detail returned from the server.'}
                  </p>
                </div>
              )}
            </div>
          )}

          {save.isError && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2">
              <p className="text-xs font-semibold text-red-800">Could not save draft</p>
              <p className="mt-0.5 text-xs text-red-700 font-mono break-all whitespace-pre-wrap">
                {describeError(save.error)}
              </p>
            </div>
          )}

          <div className="flex justify-end gap-3">
            <button type="button" onClick={() => navigate(backPath)} className="btn-secondary">Cancel</button>
            <button type="submit" disabled={isSubmitting || save.isPending} className="btn-primary">
              <Save className="w-4 h-4" />
              {(isSubmitting || save.isPending) ? 'Saving…' : 'Save draft'}
            </button>
          </div>
        </form>
      )}
    </div>
  )
}
